import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

// To do: does this also work?
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
// const rehypeRaw = React.lazy(() => import("rehype-raw"));
// const rehypeSanitize = React.lazy(() => import("rehype-sanitize"));

const AutoLink = React.lazy(() => import("../utils/auto-link"));
const InlineList = React.lazy(() => import("../utils/inline-list"));
const PersonList = React.lazy(() => import("../persons/person-list"));
const ReactMarkdown = React.lazy(() => import("react-markdown"));
const ClampedDiv = React.lazy(() => import("./clamped-div"));
const Truncate = React.lazy(() => import("react-truncate"));

const CardImage = ({ image, title }) =>
  !image ? null : (
    <div className="card-img-top">
      <GatsbyImage
        image={getImage(image)}
        alt={title}
        className="card-img-top fix-gatsby-image-wrapper"
      />
    </div>
  );

const CardTitle = ({ title, subtitle }) =>
  !title && !subtitle ? null : (
    <h5 className="card-title mb-0">
      <span className={subtitle ? "me-2" : null}>{title}</span>
      {subtitle ? (
        <span className="text-muted fw-normal">{subtitle}</span>
      ) : null}
    </h5>
  );

const CardPeople = ({ people, showLastName = true }) =>
  !people ? null : (
    <div>
      <React.Suspense fallback="loading..." key="persons">
        <PersonList
          persons={people}
          finalSep=" "
          showLastName={showLastName}
          small={true}
        />
      </React.Suspense>
    </div>
  );

const CardProperties = ({
  date,
  properties = [],
  propertySep = <span className="text-muted mx-1">•</span>,
} = {}) =>
  !date && !properties ? null : (
    <div className="text-muted">
      <InlineList sep={propertySep}>{[date, ...properties]}</InlineList>
    </div>
  );

const CardText = ({ text, lines = 5, expandable = true } = {}) =>
  !text ? null : (
    <div className="card-text">
      {!expandable ? (
        <React.Suspense fallback="Loading...">
          <Truncate lines={lines}>
            <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
              {text}
            </ReactMarkdown>
          </Truncate>
        </React.Suspense>
      ) : (
        <React.Suspense fallback="loading...">
          <ClampedDiv>
            <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
              {text}
            </ReactMarkdown>
          </ClampedDiv>
        </React.Suspense>
      )}
    </div>
  );

const CardFields = ({ fields }) =>
  !fields ? null : (
    <ul className="list-group list-group-flush">
      {Object.keys(fields).map((key) => (
        <li className="list-group-item" key={key}>
          <span className="key text-muted">{`${key}: `}</span>
          <span className="value">
            <React.Suspense fallback="Loading...">
              <ReactMarkdown
                components={{ p: React.Fragment }}
                children={fields[key]}
              />
            </React.Suspense>
          </span>
        </li>
      ))}
    </ul>
  );

const CardSecondaryLinks = ({ links, maxNumLinks = 2 }) =>
  !links ? null : (
    <>
      {links.slice(0, maxNumLinks).map((link) => (
        <AutoLink
          to={link.href}
          key={link.href}
          className="btn btn-light px-2 btn-sm override-stretched-link d-flex justify-content-center"
        >
          <span
            className="d-inline-block text-truncate"
            style={{ maxWidth: 150 }}
          >
            {link.text}
          </span>
        </AutoLink>
      ))}
      {links.length > maxNumLinks ? (
        <span className="btn btn-light disabled px-2 btn-sm rounded-5 text-truncate">
          ...
        </span>
      ) : null}
    </>
  );

const CardMainLink = ({ to, text = "View", stretch = true } = {}) =>
  !to ? null : (
    <AutoLink
      to={to}
      className={`btn btn-sm btn-primary ${stretch ? "stretched-link" : ""}`}
    >
      <span>{text}</span>
    </AutoLink>
  );

function Card({
  title,
  subtitle,
  date,
  properties,
  text,
  fields,
  mainLink,
  secondaryLinks,
  people,
  image,
  children,
  options = {},
  ...props
} = {}) {
  const defaultOpts = {
    showImage: true,
    showTitle: true,
    showProperties: true,
    showText: true,
    showPeople: true,
    showFields: true,
    showDate: true,
    showDateInFooter: false,
    showSecondaryLinks: true,
    showMainLink: true,
  };

  const {
    showFields,
    showPeople,
    showDate,
    showDateInFooter,
    showTitle,
    showText,
    showProperties,
    showImage,
    showMainLink,
    mainLinkText,
    showSecondaryLinks,
    maxNumLinks,
    propertySep,
    stretchMainLink,
    textExpandable,
    textTruncateLines,
    showLastName,
  } = { ...defaultOpts, ...options };

  if (maxNumLinks === 0) {
    showSecondaryLinks = false;
  }

  const hasTitle = showTitle && (title || subtitle);
  const hasProps =
    showProperties && ((showDate && date) || (showProperties && properties));
  const hasBody = hasTitle || hasProps || (showPeople && people);
  const hasFooter =
    (showSecondaryLinks && secondaryLinks) || (showMainLink && mainLink);

  return (
    <div className="card h-100 text-primary bg-transparent" {...props}>
      {!showImage || <CardImage image={image} title={title} />}

      {!hasBody || (
        <div
          className={`card-body d-flex flex-column gap-2 bg-white rounded ${
            hasFooter ? "pb-0" : null
          }`}
        >
          {!showTitle || <CardTitle title={title} subtitle={subtitle} />}
          {!showPeople || (
            <CardPeople people={people} showLastName={showLastName} />
          )}
          {!hasProps || (
            <CardProperties
              people={showPeople ? people : null}
              date={showDate ? date : null}
              properties={showProperties ? properties : null}
              propertySep={propertySep}
            />
          )}

          {!showText || (
            <CardText
              text={text}
              expandable={textExpandable}
              lines={textTruncateLines}
            />
          )}
        </div>
      )}

      {children}

      {!showFields || <CardFields fields={fields} />}

      {!hasFooter ? null : (
        <div
          className={`card-footer border-0 bg-white d-flex gap-2 flex-wrap ${
            hasBody ? "pb-3" : null
          }`}
        >
          {!mainLink || (
            <CardMainLink
              to={mainLink}
              text={mainLinkText}
              stretch={stretchMainLink}
            />
          )}

          {!showSecondaryLinks || (
            <CardSecondaryLinks
              links={secondaryLinks}
              maxNumLinks={maxNumLinks}
            />
          )}

          {!showDateInFooter || !date ? null : (
            <span className="text-muted d-flex flex-column justify-content-center">
              {date}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default Card;
